import React, { createContext, useContext, useMemo } from 'react';
import siteConfig from '../config/site.json';
import navigationConfig from '../config/navigation.json';
import sharedColors from '../config/colors.js';
// Note: app.json is not replicated to frontend, use appMain.json from assets if needed
import appMainJson from '../assets/data/appMain.json';

const ConfigContext = createContext(null);

/**
 * Configuration Provider component
 * Provides global access to site configuration, colors, navigation, and helper functions
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} ConfigContext provider
 */
export const ConfigProvider = ({ children }) => {
  const config = useMemo(() => {
    // Create a simple replace map from appMainJson if needed
    // In a real scenario, this would come from app.json.replace
    const replaceMap = {
      '{{COMPANY_NAME}}': appMainJson.CompanyName || 'Fleur Lamont Ltd',
      '{{APP_DISPLAY_NAME}}': appMainJson.DisplayName || 'ScaffolderJS',
      // Add more as needed
    };

    return {
      site: siteConfig,
      app: {
        main: appMainJson,
        // replace would come from app.json.replace if it was replicated
        replace: replaceMap,
        // Custom home feature flag - set to true to enable custom home component
        customHomeEnabled: appMainJson.customHomeEnabled || siteConfig.app?.customHomeEnabled || false,
        dashboardEnabled: appMainJson.dashboardEnabled || siteConfig.app?.dashboardEnabled || false,
      },
      colors: sharedColors,
      navigation: navigationConfig,

      // Helper functions
      /**
       * Get text value from config
       * @param {string} key - Key to look up (supports {{PLACEHOLDER}} format)
       * @returns {string} - Text value or key if not found
       */
      getText: (key) => {
        // Try app.replace first
        if (replaceMap[key]) {
          return replaceMap[key];
        }
        // Try site.json
        if (siteConfig[key]) {
          return siteConfig[key];
        }
        // Return key if not found
        return key;
      },

      /**
       * Get semantic color class
       * @param {string} semanticKey - Semantic color key (e.g., 'headerBg', 'formText')
       * @returns {string} - Tailwind classes or empty string
       */
      getColor: (semanticKey) => {
        return sharedColors.semantic?.[semanticKey] || '';
      },

      /**
       * Replace template placeholders in text
       * @param {string} text - Text with {{PLACEHOLDER}} placeholders
       * @returns {string} - Text with placeholders replaced
       */
      replaceTemplate: (text) => {
        if (!text || typeof text !== 'string') return text;
        return text.replace(/\{\{(\w+)\}\}/g, (match) => {
          return replaceMap[match] || match;
        });
      },

      /**
       * Get nested config value
       * @param {string} path - Dot-separated path (e.g., 'main.DisplayName')
       * @returns {any} - Config value or undefined
       */
      getConfigValue: (path) => {
        const keys = path.split('.');
        let value = { main: appMainJson, site: siteConfig };
        for (const key of keys) {
          if (value && typeof value === 'object' && key in value) {
            value = value[key];
          } else {
            return undefined;
          }
        }
        return value;
      },
    };
  }, []);

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
};

/**
 * Hook to access configuration context
 * @returns {Object} Configuration object with site, app, colors, navigation, and helper functions
 * @throws {Error} If used outside ConfigProvider
 */
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
};

export default ConfigContext;

